import { useLocalization, toast, createLazyResource, createMutation } from '@magicdoorinc/ui';
import { createEffect, createMemo, createSignal, untrack } from 'solid-js';
import { createStore, produce } from 'solid-js/store';
import { usePaymentSelectionForms } from '~/components/common/PaymentSelectionGroup/usePaymentSelectionForms';
import { useBankAccountsList } from '~/contexts/bank-accounts';
import { createMagicDoorContext } from '~/contexts/utils';
import { useOwnerDistributionsByMode } from '~/hooks/ownerDistributions';
import { OwnersRepository } from '~/repositories/ownersRepository';
import { TransactionPaymentMethod, PayoutMethod, PayoutStatus } from '~/swagger/portal/Api';
import { buildBankAccountOptions } from '~/utils/bankAccountHelpers';
import { displayName } from '~/utils/strings';
import { cloneDeep, uuid } from '~/utils/tool';
import type {
  DisburseOwnerDistributionsResultDto,
  HydratedBankAccountDto,
  HydratedPropertyDto,
  PropertyDto,
  CalculatedPropertyDistributionsDto,
  OwnerDistributionsResultDto,
  UpdateOwnerDistributionDto,
} from '~/swagger/portal/Api';
import type { OwnerDistributionsStoreData } from '~/types/ownerDistributions';

export interface EditingDistribution {
  propertyId: string;
  ownerId: string;
  ownerName: string;
  distributionAmount: number;
  memo: string;
  paymentBankAccountId: string;
  receivingBankAccountId?: string;
  paymentType: PayoutMethod;
  paymentMethod?: TransactionPaymentMethod;
  externalTransactionId?: string;
  commonMemo: string;
  checkNumber?: number;
  ownershipPercentage?: number;
  distributionDate?: string;
}

type SelectedProperty = PropertyDto | null | HydratedPropertyDto | undefined;

interface EditingDistributionGroup {
  groupId: string;
  ownerId: string;
  propertyIds: string[];
}

export interface AddOwnerDistributionsStore {
  currentStep: number;
  selectedProperties: SelectedProperty[];
  selectedCategory: PropertiesCategory;
  isValidate: boolean;
  editingDistributions: EditingDistribution[];
  isSubmitting: boolean;
  response?: OwnerDistributionsResultDto | DisburseOwnerDistributionsResultDto;
  editingDistributionGroups: EditingDistributionGroup[];
  bankAccounts: HydratedBankAccountDto[];
}

export enum PropertiesCategory {
  ALL,
  SOME,
}

export type PropertyReviewRecord = {
  property?: PropertyDto;
  isMissingOwnerInformation?: boolean;
  ownershipPercentage?: number;
  checked?: boolean;
};

const getInitialStore = () => ({
  selectedCategory: PropertiesCategory.ALL,
  currentStep: 0,
  selectedProperties: [],
  isValidate: true,
  editingDistributions: [],
  isSubmitting: false,
  editingDistributionGroups: [],
  bankAccounts: [],
});

export interface AddDistributionData {
  propertyId: string;
  ownerId: string;
  distributionAmount: number;
  paymentType: PayoutMethod;
  paymentMethod?: TransactionPaymentMethod;
  bankAccountId: string;
  paymentAccountId?: string;
  externalTransactionId?: string;
  memo?: string;
  distributionDate?: string;
}

export const [AddOwnerDistributionsProvider, useAddOwnerDistributions] = createMagicDoorContext('AddOwnerDistributions', () => {
  const ownersRepo = new OwnersRepository();
  const bankAccountsList = useBankAccountsList();
  const { submitDistributions } = useOwnerDistributionsByMode();
  const { t } = useLocalization();
  const paymentOptionsForOwnerDistributions = (t: (str: string) => string) => [
    { label: t('ACH'), value: PayoutMethod.Ach },
    { label: t('Manual'), value: PayoutMethod.Manual },
    { label: t('Print check'), value: PayoutMethod.PrintCheck },
  ];

  const { hasPaymentCheckError, getForm, clearForm } = usePaymentSelectionForms();

  const [store, setStore] = createStore<AddOwnerDistributionsStore>(getInitialStore());
  const [propertyValidationRecords, propertyValidationRecordsActions] = createLazyResource<PropertyReviewRecord[]>(async () => {
    const response = await ownersRepo.getOwnersDistributionsValidation({
      propertyIds:
        store.selectedCategory === PropertiesCategory.ALL
          ? undefined
          : store.selectedProperties.filter((property) => !!property).map((property) => property?.id),
    });
    const results = [
      ...(response.needsAttentionProperties ?? []).map((record) => ({
        property: record.property,
        isMissingOwnerInformation: record.isMissingOwnerInformation,
        ownershipPercentage: record.calculatedOwnershipPercentage,
        checked: record.isMissingOwnerInformation ? false : true,
      })),
      ...(response.validProperties ?? []).map((record) => ({
        property: record.property,
        isMissingOwnerInformation: false,
        ownershipPercentage: 100,
        checked: true,
      })),
    ];

    return results;
  });
  const bankAccountOptions = createMemo(() =>
    buildBankAccountOptions(bankAccountsList.bankAccounts(), (account) => !account.companyAccount)
  );

  const [ownerDistributions, setOwnerDistributions] = createSignal<CalculatedPropertyDistributionsDto['properties']>([]);

  const calculatePropertiesDistributions = createMutation(async () => {
    const validatedProperties = propertyValidationRecords()?.filter((record) => !record.isMissingOwnerInformation && record.checked);
    if (!validatedProperties?.length) return;
    const response = await ownersRepo.getOwnersDistributionsCalculation({
      propertyIds: validatedProperties?.filter((record) => !!record.property).map((record) => (record.property as PropertyDto).id),
    });
    setOwnerDistributions(response.properties);
    initEditingDistributionGroups();
    clearForm();
  });

  const onChangeSelectedProperties = (properties: SelectedProperty[]) => {
    setStore('selectedProperties', properties);
  };

  const onChangeIsValidate = (step: number) => {
    let isValidate = store.isValidate;

    switch (step) {
      case 0: {
        isValidate =
          store.selectedCategory === PropertiesCategory.ALL || [...new Set(store.selectedProperties)].filter((id) => !!id).length > 0;
        break;
      }

      case 1: {
        isValidate = (propertyValidationRecords() ?? []).some((record) => record.checked);

        break;
      }

      case 2: {
        isValidate = Boolean(store.editingDistributions.find((item) => item.distributionAmount > 0));
        break;
      }

      case 3: {
        isValidate =
          Boolean(store.editingDistributionGroups.length) &&
          Boolean(
            !store.editingDistributions.find((item) => {
              if (!item.paymentBankAccountId) {
                return true;
              }
              if (item.paymentType === PayoutMethod.Manual && !item.externalTransactionId) {
                return true;
              }
              if (item.paymentType === PayoutMethod.Ach && !item.receivingBankAccountId) {
                return true;
              }

              return false;
            })
          ) &&
          !hasPaymentCheckError();
        break;
      }

      default: {
        isValidate = true;
        break;
      }
    }

    setStore('isValidate', isValidate);
  };

  const onStepChange = (step: number, isBack?: boolean) => {
    if (store.currentStep === 3 && step === 4 && !isBack) {
      onSubmitOwnerDistributions();
      return;
    }

    if (step === 1 && !isBack) {
      setStore(
        'selectedProperties',
        store.selectedProperties.filter((property) => !!property)
      );
    }

    if (step === 2 && !isBack) {
      calculatePropertiesDistributions();
    }

    setStore('currentStep', step);
  };

  const onChangePropertiesCategory = (category: PropertiesCategory) => {
    setStore('selectedCategory', category);
  };

  const onChangePropertyValidationRecords = (records: PropertyReviewRecord[]) => {
    propertyValidationRecordsActions.mutate(records);
  };

  const onRemoveOwnerDistributionByPropertyId = (propertyId?: string) => {
    setOwnerDistributions(ownerDistributions()?.filter((item) => item.property?.id !== propertyId));
    setStore(
      produce((state) => {
        state.editingDistributions = state.editingDistributions.filter((item) => item.propertyId !== propertyId);
      })
    );
  };

  const onRemoveOwnerDistributionByOwnerId = (ownerId?: string) => {
    const clone = cloneDeep(ownerDistributions());

    clone?.forEach((record) => {
      record.owners = record.owners?.filter((owner) => owner.owner?.id !== ownerId);
    });
    setOwnerDistributions(clone?.filter((item) => item.owners.length));

    setStore(
      produce((state) => {
        state.editingDistributions = state.editingDistributions.filter((item) => item.ownerId !== ownerId);
      })
    );
  };

  const updateOwnerDistributionRecord = (params: {
    ownerId?: string;
    propertyId?: string;
    payload: { [key in keyof EditingDistribution]?: any };
  }) => {
    const { ownerId, propertyId, payload } = params;

    if (!ownerId && !propertyId) {
      return;
    }

    const editingRecordIndexes = store.editingDistributions
      .map((record, index) => {
        const isValid = (ownerId ? record.ownerId === ownerId : true) && (propertyId ? record.propertyId === propertyId : true);
        return isValid ? index : -1;
      })
      .filter((index) => index > -1);

    editingRecordIndexes.forEach((value) => {
      const editingRecord = store.editingDistributions[value];

      if (!editingRecord) {
        return;
      }

      setStore('editingDistributions', value, (prev) => ({ ...prev, ...payload }));
    });
  };

  const onSubmitOwnerDistribution = async (data: AddDistributionData) => {
    await ensureBankAccountsLoaded();
    const storeData: OwnerDistributionsStoreData = {
      editingDistributions: [
        {
          propertyId: data.propertyId,
          ownerId: data.ownerId,
          distributionAmount: data.distributionAmount,
          paymentBankAccountId: data.bankAccountId,
          receivingBankAccountId: data.paymentAccountId,
          paymentType: data.paymentType,
          paymentMethod: data.paymentMethod,
          externalTransactionId: data.externalTransactionId,
          commonMemo: data.memo ?? '',
          memo: data.memo ?? '',
          distributionDate: data.distributionDate,
        },
      ],
      editingDistributionGroups: [{ ownerId: data.ownerId, propertyIds: [data.propertyId] }],
      bankAccounts: store.bankAccounts,
    };
    const res = await submitDistributions(storeData);
    const hasFailures = 'disbursements' in res && (res.disbursements as any[])?.some((d) => d.status === PayoutStatus.Failed);
    if (!hasFailures) {
      toast.success(t('Owner distribution(s) have been added successfully'));
    }
    return res;
  };

  const onSubmitOwnerDistributions = async () => {
    await ensureBankAccountsLoaded();
    setStore('isSubmitting', true);
    try {
      const storeData = {
        editingDistributions: store.editingDistributions,
        editingDistributionGroups: store.editingDistributionGroups,
        bankAccounts: store.bankAccounts,
      };
      const response = await submitDistributions(storeData);
      setStore('response', response);
      const hasFailures = 'disbursements' in response && (response.disbursements as any[])?.some((d) => d.status === PayoutStatus.Failed);
      if (!hasFailures) {
        toast.success(t('Owner distribution(s) have been added successfully'));
      }
      const timer = window.setTimeout(() => {
        setStore('currentStep', untrack(() => store.currentStep) + 1);
        window.clearTimeout(timer);
      }, 100);
    } finally {
      setStore('isSubmitting', false);
    }
  };

  const onResetStore = () => {
    setStore(() => ({ ...getInitialStore() }));
  };

  function initEditingDistributionGroups() {
    const editingDistributions: EditingDistribution[] = [];

    ownerDistributions()?.forEach((property) => {
      property.owners?.forEach((record) => {
        if (property.property && record.owner) {
          const existingEditingDistribution = store.editingDistributions?.find(
            (item) => item.propertyId === property.property.id && item.ownerId === record.owner.id
          );

          editingDistributions.push({
            propertyId: property.property.id,
            ownerId: record.owner.id,
            ownerName: displayName(record.owner.firstName, record.owner.lastName),
            memo: property.defaultMemo || '',
            commonMemo: '',
            paymentBankAccountId: property.defaultBankAccountId ?? bankAccountOptions()?.[0]?.value ?? '',
            receivingBankAccountId: record.paymentAccounts[0]?.id ?? undefined,
            paymentMethod: TransactionPaymentMethod.Ach,
            distributionAmount: record.distributionAmount || 0,
            paymentType: PayoutMethod.PrintCheck,
            ownershipPercentage: record.ownershipPercentage,
            ...existingEditingDistribution,
          });
        }
      });
    });

    setStore('editingDistributions', editingDistributions);
  }

  const ensureBankAccountsLoaded = async () => {
    if (!store.bankAccounts || store.bankAccounts.length === 0) {
      const currentBankAccounts = bankAccountsList.bankAccounts();

      if (currentBankAccounts?.length) {
        setStore('bankAccounts', currentBankAccounts);
        return;
      }

      const response = await bankAccountsList.refetchBankAccounts();
      setStore('bankAccounts', response ?? bankAccountsList.bankAccounts() ?? []);
    }
  };

  const onEditOwnerDistribution = async (ownerDistributionId: string, payload: UpdateOwnerDistributionDto) => {
    const res = await ownersRepo.editOwnerDistributions(ownerDistributionId, payload);
    toast.success(t('{name} has been edited successfully', { name: t('Owner distribution') }));
    return res;
  };

  createEffect(() => {
    const bankAccounts = bankAccountsList.bankAccounts();

    setStore('bankAccounts', bankAccounts ?? []);
  });

  createEffect(() => {
    if (!bankAccountsList.bankAccounts() && !bankAccountsList.bankAccounts.loading) {
      void bankAccountsList.refetchBankAccounts();
    }
  });

  createEffect(() => {
    const ownerGroups: { ownerId: string; propertyDefaultBankId: string }[] = [];

    store.editingDistributions.forEach((distribution) => {
      if (
        !ownerGroups.find(
          (item) => item.ownerId === distribution.ownerId && item.propertyDefaultBankId === distribution.paymentBankAccountId
        )
      ) {
        ownerGroups.push({ ownerId: distribution.ownerId, propertyDefaultBankId: distribution.paymentBankAccountId });
      }
    });

    setStore(
      'editingDistributionGroups',
      ownerGroups.map((group) => ({
        groupId: uuid(),
        ownerId: group.ownerId,
        propertyIds: store.editingDistributions
          .filter(
            (distribution) => distribution.ownerId === group.ownerId && distribution.paymentBankAccountId === group.propertyDefaultBankId
          )
          .map((distribution) => distribution.propertyId),
      }))
    );
  });

  return {
    store,
    setStore,
    paymentOptionsForOwnerDistributions,
    onChangeSelectedProperties,
    onChangeIsValidate,
    onStepChange,
    onChangePropertiesCategory,
    propertyValidationRecords,
    propertyValidationRecordsActions,
    ownerDistributions,
    bankAccountOptions,
    updateOwnerDistributionRecord,
    onChangePropertyValidationRecords,
    onResetStore,
    onSubmitOwnerDistribution,
    onRemoveOwnerDistributionByPropertyId,
    onRemoveOwnerDistributionByOwnerId,
    calculatePropertiesDistributions,
    getForm,
    clearForm,
    onEditOwnerDistribution,
  };
});
